import Phaser from 'phaser';
import { BOSS_COLOR, GAME_FONT_FAMILY } from '@shared/constants';
import type { NpcDisplayInfo } from '@shared/data/npcs';
import { createCloseButton, getIconTexture } from '../../sprites/IconAtlases';

/**
 * Panel desplegable con info de un NPC (stats + spells + tabla de loot).
 *
 * Comportamiento (definido con el usuario):
 *  - Se abre con right-click sobre un NPC.
 *  - Es draggable desde el header (mismo patrón que InvokePanel/TargetPanel).
 *  - Otro right-click sobre otro NPC reemplaza el contenido.
 *  - X cierra. Si el NPC apuntado muere o desaparece, el panel queda con la
 *    última info conocida hasta que el usuario lo cierre.
 *
 * Diseño: usamos texto multilínea por sección en vez de N objetos Phaser por
 * entry. Esto reduce el costo de creación/cleanup cuando se hace replace de
 * contenido (un NPC con 10 spells y 20 loot entries = 1 text por sección, no
 * 30 objetos). El trade-off es menos interactividad por item, que para esta
 * pantalla no se necesita.
 */
function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

export interface NpcInfoPanelCallbacks {
  onMove?: (x: number, y: number) => void;
}

export interface NpcInfoPanelOptions {
  initialX: number;
  initialY: number;
  width: number;
  height: number;
}

interface PanelElements {
  bg: Phaser.GameObjects.Rectangle;
  title: Phaser.GameObjects.Text;
  bossMarker: Phaser.GameObjects.Text;
  statsHeader: Phaser.GameObjects.Text;
  statsBody: Phaser.GameObjects.Text;
  spellsHeader: Phaser.GameObjects.Text;
  spellsBody: Phaser.GameObjects.Text;
  lootHeader: Phaser.GameObjects.Text;
  lootHeaderIcon: Phaser.GameObjects.Sprite | null;
  lootBody: Phaser.GameObjects.Text;
  closeButton: Phaser.GameObjects.Sprite;
  panelX: number;
  panelY: number;
  panelWidth: number;
  panelHeight: number;
}

const DEPTH_BG = 1100;
const DEPTH_FG = 1101;
const DEPTH_BTN = 1102;

export class NpcInfoPanel {
  private readonly scene: Phaser.Scene;
  private readonly callbacks: NpcInfoPanelCallbacks;
  private elements: PanelElements | null = null;

  private isOpen = false;
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  /** ID del NPC actualmente mostrado (null si nunca recibió info). */
  private currentNpcId: string | null = null;
  /** Cache para que el marker boss respete su flag al re-mostrar el panel. */
  private currentIsBoss = false;

  constructor(scene: Phaser.Scene, callbacks: NpcInfoPanelCallbacks, opts: NpcInfoPanelOptions) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.buildPanel(opts);
    this.setVisible(false);
  }

  open(): void {
    this.setVisible(true);
    this.isOpen = true;
  }

  close(): void {
    this.setVisible(false);
    this.isOpen = false;
  }

  isOpenPanel(): boolean {
    return this.isOpen;
  }

  isDraggingPanel(): boolean {
    return this.isDragging;
  }

  getCurrentNpcId(): string | null {
    return this.currentNpcId;
  }

  /** Reemplaza el contenido con el del nuevo NPC. Si no estaba abierto, se abre. */
  showNpcInfo(info: NpcDisplayInfo): void {
    if (!this.elements) return;
    this.currentNpcId = info.npcId;
    this.currentIsBoss = info.isBoss;
    this.elements.title.setText(info.name);
    this.elements.statsBody.setText(formatStats(info));
    this.elements.spellsBody.setText(formatSpells(info));
    this.elements.lootBody.setText(formatLoot(info));
    if (!this.isOpen) {
      this.open();
    } else {
      // Si ya estaba abierto, refrescamos visibilidad del marker boss.
      this.elements.bossMarker.setVisible(this.currentIsBoss);
    }
  }

  handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.isDragging || !this.elements) return;
    const newX = pointer.x - this.dragOffsetX;
    const newY = pointer.y - this.dragOffsetY;
    this.move(newX, newY);
    this.callbacks.onMove?.(newX, newY);
  }

  handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.isDragging) {
      this.isDragging = false;
      pointer.event.stopPropagation();
    }
  }

  isPointerOver(pointer: Phaser.Input.Pointer): boolean {
    if (!this.elements || !this.isOpen) return false;
    const { panelX, panelY, panelWidth, panelHeight } = this.elements;
    const left = panelX - panelWidth / 2;
    const right = panelX + panelWidth / 2;
    const top = panelY - panelHeight / 2;
    const bottom = panelY + panelHeight / 2;
    return pointer.x >= left && pointer.x <= right && pointer.y >= top && pointer.y <= bottom;
  }

  private buildPanel(opts: NpcInfoPanelOptions): void {
    const { initialX, initialY, width, height } = opts;
    const panelX = initialX;
    const panelY = initialY;
    const panelWidth = width;
    const panelHeight = height;
    const baseX = panelX - panelWidth / 2;
    const baseY = panelY - panelHeight / 2;
    const padding = 10;
    const contentX = baseX + padding;
    const contentWidth = panelWidth - padding * 2;

    const bg = this.scene.add
      .rectangle(panelX, panelY, panelWidth, panelHeight, 0x111111, 0.92)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x666666)
      .setDepth(DEPTH_BG)
      .setScrollFactor(0)
      // Fondo interactivo: absorbe hover/click de objetos del mundo debajo
      // (Phaser topOnly) para que la ventana no deje pasar clicks ni cambie el cursor.
      .setInteractive()
      .setVisible(false);

    const title = this.scene.add
      .text(panelX, baseY + padding, '', withGameFont({
        fontSize: '14px',
        color: '#ffffff',
        fontStyle: 'bold',
      }))
      .setOrigin(0.5, 0)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true })
      .setVisible(false);

    // Marker boss (debajo del título, centrado, dorado).
    const bossMarker = this.scene.add
      .text(panelX, baseY + padding + 18, '★ BOSS ★', withGameFont({
        fontSize: '11px',
        color: BOSS_COLOR,
        fontStyle: 'bold',
      }))
      .setOrigin(0.5, 0)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0)
      .setVisible(false);

    // Si hay marker boss, los headers empiezan un poco más abajo para no pisarlos.
    let cursorY = baseY + padding + 36;

    const statsHeader = this.scene.add
      .text(contentX, cursorY, 'STATS', withGameFont({
        fontSize: '11px',
        color: '#88ccff',
        fontStyle: 'bold',
      }))
      .setOrigin(0, 0)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0)
      .setVisible(false);
    cursorY += 16;

    const statsBody = this.scene.add
      .text(contentX, cursorY, '', withGameFont({
        fontSize: '11px',
        color: '#ffffff',
        wordWrap: { width: contentWidth },
      }))
      .setOrigin(0, 0)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0)
      .setVisible(false);
    cursorY += 90;

    const spellsHeader = this.scene.add
      .text(contentX, cursorY, 'HECHIZOS', withGameFont({
        fontSize: '11px',
        color: '#cc99ff',
        fontStyle: 'bold',
      }))
      .setOrigin(0, 0)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0)
      .setVisible(false);
    cursorY += 16;

    const spellsBody = this.scene.add
      .text(contentX, cursorY, '', withGameFont({
        fontSize: '11px',
        color: '#ffffff',
        wordWrap: { width: contentWidth },
      }))
      .setOrigin(0, 0)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0)
      .setVisible(false);
    cursorY += 70;

    // Header "TABLA DE LOOT" con sprite `loot` prefijo.
    const lootIconTex = getIconTexture('loot');
    let lootHeaderTextX = contentX;
    let lootHeaderIcon: Phaser.GameObjects.Sprite | null = null;
    if (lootIconTex && this.scene.textures.exists(lootIconTex.textureKey)) {
      lootHeaderIcon = this.scene.add
        .sprite(contentX, cursorY + 6, lootIconTex.textureKey, lootIconTex.frame)
        .setOrigin(0, 0.5)
        .setDisplaySize(12, 12)
        .setDepth(DEPTH_FG)
        .setScrollFactor(0)
        .setVisible(false);
      lootHeaderTextX = contentX + 12 + 4;
    }
    const lootHeader = this.scene.add
      .text(lootHeaderTextX, cursorY, 'TABLA DE BOTÍN', withGameFont({
        fontSize: '11px',
        color: '#ffcc66',
        fontStyle: 'bold',
      }))
      .setOrigin(0, 0)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0)
      .setVisible(false);
    cursorY += 16;

    const lootBody = this.scene.add
      .text(contentX, cursorY, '', withGameFont({
        fontSize: '11px',
        color: '#ffffff',
        wordWrap: { width: contentWidth },
      }))
      .setOrigin(0, 0)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0)
      .setVisible(false);

    const closeButton = createCloseButton(this.scene, panelX + panelWidth / 2 - 12, baseY + padding, 14)!
      .setOrigin(0.5, 0)
      .setDepth(DEPTH_BTN)
      .setVisible(false)
      .setAlpha(0.75);

    closeButton.on('pointerover', () => closeButton.setAlpha(1));
    closeButton.on('pointerout', () => closeButton.setAlpha(0.75));
    closeButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.close();
      pointer.event.stopPropagation();
    });

    title.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragOffsetX = pointer.x - (this.elements?.panelX ?? panelX);
      this.dragOffsetY = pointer.y - (this.elements?.panelY ?? panelY);
      pointer.event.stopPropagation();
    });

    this.elements = {
      bg,
      title,
      bossMarker,
      statsHeader,
      statsBody,
      spellsHeader,
      spellsBody,
      lootHeader,
      lootHeaderIcon,
      lootBody,
      closeButton,
      panelX,
      panelY,
      panelWidth,
      panelHeight,
    };
  }

  private setVisible(visible: boolean): void {
    if (!this.elements) return;
    const e = this.elements;
    e.bg.setVisible(visible).setActive(visible);
    e.title.setVisible(visible).setActive(visible);
    e.bossMarker.setVisible(visible && this.currentIsBoss).setActive(visible);
    e.statsHeader.setVisible(visible).setActive(visible);
    e.statsBody.setVisible(visible).setActive(visible);
    e.spellsHeader.setVisible(visible).setActive(visible);
    e.spellsBody.setVisible(visible).setActive(visible);
    e.lootHeader.setVisible(visible).setActive(visible);
    e.lootHeaderIcon?.setVisible(visible).setActive(visible);
    e.lootBody.setVisible(visible).setActive(visible);
    e.closeButton.setVisible(visible).setActive(visible);
  }

  private move(newX: number, newY: number): void {
    if (!this.elements) return;
    const e = this.elements;
    const dx = newX - e.panelX;
    const dy = newY - e.panelY;
    e.panelX = newX;
    e.panelY = newY;
    e.bg.setPosition(newX, newY);
    e.title.x += dx;
    e.title.y += dy;
    e.bossMarker.x += dx;
    e.bossMarker.y += dy;
    e.statsHeader.x += dx;
    e.statsHeader.y += dy;
    e.statsBody.x += dx;
    e.statsBody.y += dy;
    e.spellsHeader.x += dx;
    e.spellsHeader.y += dy;
    e.spellsBody.x += dx;
    e.spellsBody.y += dy;
    e.lootHeader.x += dx;
    e.lootHeader.y += dy;
    if (e.lootHeaderIcon) {
      e.lootHeaderIcon.x += dx;
      e.lootHeaderIcon.y += dy;
    }
    e.lootBody.x += dx;
    e.lootBody.y += dy;
    e.closeButton.x += dx;
    e.closeButton.y += dy;
  }
}

// ============================================================================
// FORMATTERS
// ============================================================================

export function formatStats(info: NpcDisplayInfo): string {
  const lines = [
    `HP máx: ${info.maxHp}`,
    `Daño: ${info.minDamage} - ${info.maxDamage}`,
    `Def física: ${info.physDef}    Def mágica: ${info.magicDef}`,
    `Evasión: ${(info.evasion * 100).toFixed(1)}%`,
    `Vel. ataque: ${info.attackSpeed.toFixed(2)}x    Vel. mov: ${info.moveSpeed.toFixed(2)}x`,
    `EXP: ${info.expReward}${info.expOnHit ? ' (por golpe)' : ''}`,
  ];
  return lines.join('\n');
}

export function formatSpells(info: NpcDisplayInfo): string {
  if (info.spells.length === 0) return '(ninguno)';
  return info.spells
    .map((s) => {
      let when: string;
      switch (s.priority) {
        case 'when_hp_below':
          when = `HP < ${Math.round((s.hpThreshold ?? 0.5) * 100)}%`;
          break;
        case 'on_target':
          when = 'al target';
          break;
        case 'periodic':
          when = s.periodMs ? `cada ${(s.periodMs / 1000).toFixed(1)}s` : 'rotación';
          break;
        default:
          when = '';
      }
      return `• ${s.spellName}  (cd ${s.cooldownSeconds.toFixed(1)}s, ${when})`;
    })
    .join('\n');
}

export function formatLoot(info: NpcDisplayInfo): string {
  if (info.loot.length === 0) return '(sin drops)';
  return info.loot
    .map((l) => {
      const qty = l.minQty === l.maxQty ? `${l.minQty}` : `${l.minQty}-${l.maxQty}`;
      const pct = (l.chance * 100).toFixed(l.chance < 0.1 ? 1 : 0);
      return `• ${l.itemName}  (${pct}%, x${qty})`;
    })
    .join('\n');
}
